import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getFileHeaderIdAPI, updateFileHeaderAPI } from 'src/api/DataCenterApi';
import { fetchFileRecordsRequest, updateColumnVisibilityRequest } from 'src/containers/data-center-container/Data-center/actions/DataCenterActions';

const LearningDynamicTable = ({ dataCenterId, searchTerm, containerRef }) => {
  const { data: tableData, columns, loading, totalRecords, headerId } = useSelector(
    (state) => state.dataCenter?.fileRecords
  );
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filters, setFilters] = useState({});
  const [itemCount, setItemCount] = useState(tableData.length);
  const listRef = useRef(null);
  const dispatch = useDispatch()

  const fetchMoreData = () => {
    if (loading || tableData.length >= totalRecords) return;

    dispatch(fetchFileRecordsRequest({ dataCenterId }));
  };

  const [columnWidths, setColumnWidths] = useState(() => {
    const saved = localStorage.getItem("columnWidths");
    if (saved) return JSON.parse(saved);
  
    const initialWidths = {};
    columns.forEach(col => {
      if (col.visible) {
        initialWidths[col.fieldName] = 150;
      }
    });
    return initialWidths;
  });
  
  const handleMouseDown = (e, fieldName) => {
    const startX = e.clientX;
    const startWidth = columnWidths[fieldName];
  
    let animationFrameId = null;

    const onMouseMove = (e) => {
      if (animationFrameId) return;
    
      animationFrameId = requestAnimationFrame(() => {
        const newWidth = Math.max(50, startWidth + (e.clientX - startX));
        setColumnWidths(prev => {
          const updated = {
            ...prev,
            [fieldName]: newWidth,
          };
          localStorage.setItem("columnWidths", JSON.stringify(updated));
          return updated;
        });
    
        animationFrameId = null;
      });
    };
    
  
    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };
  
  // Table Scroll Code Start
  const elementRef = useRef(null);
  const [isScrollable, setIsScrollable] = useState(false);
  const debounceTimeoutRef = useRef(null); // Store the debounce timeout ID
  
  useEffect(() => {
    const handleScroll = () => {
      // Clear the previous timeout to prevent multiple updates
      clearTimeout(debounceTimeoutRef.current);
  
      debounceTimeoutRef.current = setTimeout(() => {
        if (elementRef.current) {
          const { top, bottom } = elementRef.current.getBoundingClientRect();
          const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  
          // Check if the table <thead> is within the viewport range
          const shouldScroll = top <= 100 && bottom >= viewportHeight - 100;
          setIsScrollable(shouldScroll);
        }
      }, 100); // Adjust the debounce delay as needed (e.g., 100ms)
    };
  
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(debounceTimeoutRef.current); // Cleanup on unmount
    };
  }, []);
  // Table Scroll Code Start

  const filteredData = useMemo(() => {
    let data = tableData;
    if (searchTerm) {
      data = data.filter((row) =>
        columns.some((col) => {
          const cellValue = row?.fields[col?.fieldName]?.toString().toLowerCase();
          return cellValue?.includes(searchTerm.toLowerCase());
        })
      );
    }

    Object.keys(filters).forEach((columnField) => {
      const filterValue = filters[columnField].toLowerCase();
      data = data.filter((row) => {
        const cellValue = row?.fields[columnField]?.toString().toLowerCase();
        return cellValue?.includes(filterValue);
      });
    });

    if (sortConfig.key) {
      data = data.sort((a, b) => {
        const aValue = a.fields[sortConfig.key];
        const bValue = b.fields[sortConfig.key];

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return data;
  }, [tableData, searchTerm, columns, sortConfig, filters]);

  const handleScroll = ({ scrollOffset, scrollHeight, clientHeight }) => {
    if (scrollHeight - scrollOffset <= clientHeight * 1.5) {  // If the user is near the bottom
      fetchMoreData();
    }
  };

  const UIDBadge = () => (
    <span className="bgUidBlue d-inline-flex align-items-center gap-1">
      <svg
        width={10}
        height={10}
        viewBox="0 0 10 10"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g clipPath="url(#clip0_843_840)">
          <path
            d="M9.97927 6.40631C9.93759 6.5508 9.84589 6.66751 9.71529 6.74254L8.82052 7.24272V8.26531C8.82052 8.57098 8.57043 8.82107 8.26477 8.82107H7.2394L6.74199 9.71584C6.66697 9.84644 6.55026 9.93814 6.40576 9.97982C6.26404 10.0187 6.11399 10.0021 5.98339 9.9298L4.9997 9.3796L4.01601 9.9298C3.92987 9.97704 3.83817 9.99927 3.74369 9.99927C3.69364 9.99927 3.64365 9.99371 3.59363 9.97982C3.44914 9.93814 3.33243 9.84644 3.2574 9.71584L2.76 8.82107H1.73463C1.42896 8.82107 1.17887 8.57098 1.17887 8.26531V7.24272L0.284106 6.74254C0.153503 6.66751 0.061803 6.5508 0.0201213 6.40631C-0.0187817 6.26459 -0.0021089 6.11454 0.0701394 5.98393L0.620338 5.00024L0.0701394 4.01655C-0.0799148 3.74701 0.0173426 3.408 0.284106 3.25795L1.17887 2.75777V1.73518C1.17887 1.42951 1.42896 1.17942 1.73463 1.17942H2.76L3.2574 0.284651C3.33243 0.154049 3.44914 0.0623487 3.59363 0.020667C3.73535 -0.0182359 3.88541 -0.00156327 4.01601 0.0707128L4.9997 0.620912L5.98339 0.0707128C6.11399 -0.00153548 6.26402 -0.0182082 6.40576 0.020667C6.55026 0.0623487 6.66697 0.154049 6.74199 0.284651L7.2394 1.17942H8.26477C8.57043 1.17942 8.82052 1.42951 8.82052 1.73518V2.75777L9.71529 3.25795C9.98205 3.408 10.0793 3.74701 9.92926 4.01655L9.37906 5.00024L9.92926 5.98393C10.0015 6.11454 10.0181 6.26459 9.97927 6.40631Z"
            fill="white"
          />
          <path
            d="M4.16696 7.25C4.071 7.25 3.97508 7.21336 3.90184 7.14013L2.02685 5.26513C1.88037 5.11866 1.88037 4.88136 2.02685 4.73485C2.17332 4.58834 2.41062 4.58837 2.55713 4.73485L4.167 6.34472L7.65186 2.85986C7.79834 2.71338 8.03564 2.71338 8.18215 2.85986C8.32866 3.00633 8.32862 3.24363 8.18215 3.39014L4.43216 7.14013C4.35893 7.21336 4.26289 7.25 4.16696 7.25Z"
            fill="url(#paint0_linear_843_840)"
          />
        </g>
        <defs>
          <linearGradient
            id="paint0_linear_843_840"
            x1="5.1045"
            y1="7.25006"
            x2="5.1045"
            y2="2.75006"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#0064E1" />
            <stop offset="0.9944" stopColor="#26A5FF"></stop>
          </linearGradient>
          <clipPath id="clip0_843_840">
            <rect width={10} height={10} fill="white"></rect>
          </clipPath>
        </defs>
      </svg>
      UID
    </span>
  );

  const toggleColumnVisibility = (fieldName) => {
    if (!columns?.length) return;

    const updatedColumns = columns.map(col =>
      col.fieldName === fieldName
        ? { ...col, visible: !col.visible }
        : col
    );
    dispatch(updateColumnVisibilityRequest(updatedColumns));
  };

  const setColumns = (columns) => {
  }

  const renameColumn = async (fieldName, newName) => {
    try {
      console.log(newName, headerId)
      const res = await getFileHeaderIdAPI(headerId, fieldName)
      if (res?.data?.success) {

        const fieldHeaderId = res?.data?.data?.headerId

        let updatedHeader = {
          headerId: fieldHeaderId,
          updates: {
            headerValue: newName
          }
        }

        const response = await updateFileHeaderAPI(headerId, updatedHeader)
        dispatch(fetchFileRecordsRequest({ dataCenterId }));
      }
    } catch (error) {
      console.log('error:', error)
    }
  };

  const ColumnDropdown = ({ column, isLastColumn, uidHeaderName }) => {
    const [showSubmenu, setShowSubmenu] = useState(false);
    const [filterValue, setFilterValue] = useState(filters[column.fieldName] || "");
    const submenuRef = useRef(null);
    const menuTriggerRef = useRef(null);
    const inputRef = useRef(null);

    const handleFilterChange = (e) => {
      setFilterValue(e.target.value);
    }

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (submenuRef.current && !submenuRef.current.contains(event.target) &&
          menuTriggerRef.current && !menuTriggerRef.current.contains(event.target)) {
          setShowSubmenu(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    const toggleSubmenu = (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (submenuRef.current) {
        // Force left alignment for last column
        if (isLastColumn) {
          submenuRef.current.style.right = '100%';
          submenuRef.current.style.left = 'auto';
        } else {
          const triggerRect = e.currentTarget.getBoundingClientRect();
          const submenuWidth = submenuRef.current.offsetWidth;
          const windowWidth = window.innerWidth;

          if (triggerRect.left + submenuWidth > windowWidth) {
            submenuRef.current.style.right = '100%';
            submenuRef.current.style.left = 'auto';
          } else {
            submenuRef.current.style.left = '100%';
            submenuRef.current.style.right = 'auto';
          }
        }
      }

      setShowSubmenu(!showSubmenu);
    };

    const handleToggleAllColumns = () => {
      const allVisible = columns.every(col => col.visible);
      setColumns(columns.map(col => ({ ...col, visible: !allVisible })));
    };

    const handleSortClick = (columnField) => {
      setSortConfig((prevState) => {
        const newDirection = prevState.key === columnField && prevState.direction === 'asc' ? 'desc' : 'asc';
        return { key: columnField, direction: newDirection };
      });
    };

    const applyFilter = () => {
      setFilters((prevFilters) => ({
        ...prevFilters,
        [column.fieldName]: filterValue,
      }));
      setShowSubmenu(false);
    };



    

    return (
      <div className="nav-item dropdown w-100">
        <a
          className="nav-link w-100 d-flex justify-content-between align-items-center"
          href="#"
          id={`${column.order}-menu`}
          data-bs-toggle="dropdown"
          aria-haspopup="true"
          aria-expanded="false"
          ref={menuTriggerRef}
        >
          {column.headerValue}
          <div className="d-flex align-items-center gap-7">
            {(column.isUid) && (
              <div className="d-flex align-items-center gap-1 ms-2">
                <UIDBadge />
                 
              </div>
            )}
           <span className="mdi mdi-chevron-down" />
          </div>
        </a>
        <ul className="dropdown-menu" aria-labelledby={`${column.order}-menu`}>
          <li>
            <a
              className="dropdown-item d-flex align-items-center gap-10"
              href="#"
            // data-bs-toggle="modal"
            // data-bs-target="#addfilter"
            >
              <img
                className="img-fluid"
                src="/assets/images/data-center/filter-icon.svg"
                alt="Filter"
              />
              Filter
            </a>
          </li>

          <li>
            <a className="dropdown-item  d-flex align-items-center gap-10" href="#">
              <input
                type="text"
                className="form-control"
                value={filterValue}
                onChange={handleFilterChange}
                placeholder="Filter"
                ref={inputRef}
              />
            </a>
          </li>
          <li>
            <a
              href="#"
              className="dropdown-item"
              onClick={applyFilter} // Apply the filter when clicked
            >
              Apply Filter
            </a>
          </li>

          <li>
            <a className="dropdown-item d-flex align-items-center gap-10" href="#"
              onClick={() => handleSortClick(column.fieldName)}
            >
              <img
                className="img-fluid"
                src="/assets/images/data-center/sort-icon.svg"
                alt="Sort"
              />
              Sort
            </a>
          </li>
          <li>
            <a href="#" className="dropdown-item d-flex align-items-center gap-10">
              <div className="tbDorpDivider" />
            </a>
          </li>
          <li>
            <a
              className="dropdown-item d-flex align-items-center gap-10"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                toggleColumnVisibility(column.fieldName);
              }}
            >
              <img
                className="img-fluid"
                src="/assets/images/data-center/hide-cols-icon.svg"
                alt="Hide Column"
              />
              {column.visible ? 'Hide Column' : 'Show Column'}
            </a>
          </li>
          <li className="show-all-cols">
            <a
              className="dropdown-item all-cols-menu d-flex align-items-center gap-10"
              href="#"
              onClick={toggleSubmenu}
            >
              <img
                className="img-fluid"
                src="/assets/images/data-center/show-cols-icon.svg"
                alt="Show Columns"
              />
              Show All Columns
              <span className="mdi mdi-chevron-right" />
            </a>
            <ul
              ref={submenuRef}
              className={`cols-sec space-y-3 ${showSubmenu ? '' : 'hide'}`}
              onClick={(e) => e.stopPropagation()}
            >
              <li>
                <form className="tbshowcolsInput">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`show-all-${column.order}`}
                    checked={columns.every(col => col.visible)}
                    onChange={handleToggleAllColumns}
                  />
                  <label htmlFor={`show-all-${column.order}`}>
                    Show All Columns
                  </label>
                </form>
              </li>
              <li>
                <a href="#" className="dropdown-item">
                  <div className="tbDorpDivider" />
                </a>
              </li>
              {columns.map((col) => (
                <li key={col.order}>
                  <form className="tbshowcolsInput">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`col-${col.order}`}
                      checked={col.visible}
                      onChange={() => toggleColumnVisibility(col.fieldName)}
                    />
                    <label htmlFor={`col-${col.order}`}>
                      {col.headerValue}
                    </label>
                  </form>
                </li>
              ))}
            </ul>
          </li>
          <li>
            <a href="#" className="dropdown-item">
              <div className="tbDorpDivider" />
            </a>
          </li>
          <li>
            <a
              className="dropdown-item  d-flex align-items-center gap-10"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                const newName = prompt('Enter new column name:', column.headerValue);
                if (newName) {
                  renameColumn(column.fieldName, newName);
                }
              }}
            >
              <img
                className="img-fluid"
                src="/assets/images/data-center/rename-icon.svg"
                alt="Rename"
              />
              Rename Column
            </a>
          </li>
        </ul>
      </div>
    );
  };

  
  
  return (
    <>  <style>
        {`
          .cols-sec {
            position: absolute;
            top: 0;
            min-width: 200px;
            background: white;
            border: 1px solid #ddd;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
            z-index: 1000;
            padding: 10px;
            display: none;
          }

          .cols-sec:not(.hide) {
            display: block;
          }

          .show-all-cols {
            position: relative;
          }

          .dropdown-menu {
            overflow: visible;
          }
        `}
      </style>
      <div className={`table-body-scrollable ${isScrollable ? 'scrollable' : ''}`}>
      <table className="table basic-border-table mb-0 table-hover" ref={elementRef}>
        <thead>
          <tr>
            <th className="w30px">
              <label className="form-check-label">S.No.</label>
            </th>
            {columns.filter(col => col.visible).map((column, index, visibleColumns) => (
  <th
    key={column.order}
    style={{ width: columnWidths[column.fieldName], minWidth: 50, position: 'relative' }}
  >
    <div className="d-flex align-items-center gap-2">
      <ColumnDropdown column={column} isLastColumn={index === visibleColumns.length - 1} />
    </div>
    <div
      onMouseDown={(e) => handleMouseDown(e, column.fieldName)}
      style={{
        position: "absolute",
        right: 0,
        top: 0,
        height: "100%",
        width: "5px",
        cursor: "col-resize",
        zIndex: 10,
      }}
    />
  </th>
))}

          </tr>
        </thead>
        <tbody id="table-body">
          {filteredData.map((row, index) => (
            <tr key={row.id}>
              <td>
                <label className="form-check-label">{index + 1}</label>
              </td>
              {columns.filter(col => col.visible).map((column) => {
                return <td  key={`${row.id}-${column.order}`}
                style={{ width: columnWidths[column.fieldName], minWidth: 50 }}>
                  {row.fields[column.fieldName]}
                </td>
              })}
            </tr>
          ))}
        </tbody>

        <tfoot>
          <tr>
            <td colSpan="1"></td>
            <td colSpan={columns.filter(col => col.visible).length - 1} className="fw-bold">
              Total Rows
            </td>
            <td className="text-center totalRowNumber">
              <span className="d-block fw-bold">{totalRecords}</span>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
    </>
  );
};

export default LearningDynamicTable;